/**
 * CardPaymentAddModal — credit-card add form.
 *
 * Card fields are Stripe Elements (iframes): the PAN/CVC go browser → Stripe
 * directly and never touch our code (PCI SAQ A). Adding a card mints a
 * SetupIntent and confirms it (useConfirmCardSetup), which runs AVS/CVC + Radar
 * + any 3DS challenge; the verified card is then set as default (finalizeCard).
 */

import React, {FormEvent, ReactElement, useState} from "react";
import {PatternFormat} from "react-number-format";
import {NumberFormatValues} from "react-number-format/types/types";
import {FiX, FiCreditCard} from "react-icons/fi";
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    Elements,
    useElements,
} from "@stripe/react-stripe-js";
import {StripeElementChangeEvent, StripeElementStyle} from "@stripe/stripe-js";
import {useDispatch} from "react-redux";
import {addError} from "@/redux/meta/metaActions";
import getStripePromise from "@/utils/getStripePromise";
import {useConfirmCardSetup} from "@/hooks/useConfirmCardSetup";
import {useCreateStripePaymentMethod} from "@/hooks/useCreateStripePaymentMethod";
import {Modal} from "@/widgets/Modal";
import AutofillTextInput from "@/components/inputs/AutofillTextInput";
import "./CardPaymentAddModal.scss";

const ELEMENT_STYLE: StripeElementStyle = {
    base: {
        color: "#FFFFFF",
        fontFamily: "inherit",
        fontSize: "16px",
        "::placeholder": {color: "#87878D"},
    },
    invalid: {color: "#F0616D"},
};

interface CardPaymentAddModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (paymentMethodId: string) => void;
}

function CardPaymentAddModalForm(props: CardPaymentAddModalProps): ReactElement {
    const dispatch = useDispatch();
    const elements = useElements();
    const confirmCardSetup = useConfirmCardSetup();
    const {mutateAsync: finalizeCard} = useCreateStripePaymentMethod();

    const [name, setName] = useState<string>("");
    const [postalCode, setPostalCode] = useState<string>("");
    const [complete, setComplete] = useState({number: false, expiry: false, cvc: false});
    const [submitting, setSubmitting] = useState<boolean>(false);

    const isValid = complete.number && complete.expiry && complete.cvc &&
        !!name.trim() && postalCode.length === 5;

    function onElementChange(key: "number" | "expiry" | "cvc") {
        return (e: StripeElementChangeEvent) => setComplete((prev) => ({...prev, [key]: e.complete}));
    }

    function resetForm(): void {
        setName("");
        setPostalCode("");
        elements?.getElement(CardNumberElement)?.clear();
        elements?.getElement(CardExpiryElement)?.clear();
        elements?.getElement(CardCvcElement)?.clear();
        setComplete({number: false, expiry: false, cvc: false});
    }

    function handleClose(): void {
        resetForm();
        props.onClose();
    }

    async function onFormSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();
        if (!isValid || submitting) {
            return;
        }
        setSubmitting(true);
        try {
            const paymentMethodId = await confirmCardSetup({name, postalCode});
            await finalizeCard(paymentMethodId);
            resetForm();
            props.onSubmit(paymentMethodId);
        } catch (err) {
            dispatch(await addError(err));
        } finally {
            setSubmitting(false);
        }
    }

    function postalCodeOnChange(v: NumberFormatValues): void {
        setPostalCode(v.value);
    }

    return (
        <Modal
            isOpen={props.isOpen}
            toggle={handleClose}
            size="sm"
            contentClassName="card-add-modal"
            ariaLabel="Add Credit Card"
        >
            <header className="card-add-modal__head">
                <span className="card-add-modal__eyebrow">
                    <FiCreditCard aria-hidden="true" />
                    Payment Method
                </span>
                <button
                    type="button"
                    className="card-add-modal__close"
                    onClick={handleClose}
                    aria-label="Close"
                >
                    <FiX aria-hidden="true" />
                </button>
            </header>

            <h2 className="card-add-modal__title">Add Credit Card</h2>

            <form className="card-add-modal__form" onSubmit={onFormSubmit}>
                <label className="card-add-modal__field card-add-modal__field--full">
                    <span className="card-add-modal__lbl">Name on Card</span>
                    <AutofillTextInput
                        type="text"
                        className="card-add-modal__input"
                        placeholder="Full name"
                        value={name}
                        onValueChange={setName}
                        autoComplete="name"
                    />
                </label>

                <label className="card-add-modal__field card-add-modal__field--full">
                    <span className="card-add-modal__lbl">Card Number</span>
                    <div className="card-add-modal__input">
                        <CardNumberElement
                            options={{style: ELEMENT_STYLE, showIcon: true}}
                            onChange={onElementChange("number")}
                        />
                    </div>
                </label>

                <div className="card-add-modal__row">
                    <label className="card-add-modal__field">
                        <span className="card-add-modal__lbl">Exp. Date</span>
                        <div className="card-add-modal__input">
                            <CardExpiryElement
                                options={{style: ELEMENT_STYLE}}
                                onChange={onElementChange("expiry")}
                            />
                        </div>
                    </label>

                    <label className="card-add-modal__field">
                        <span className="card-add-modal__lbl">Security</span>
                        <div className="card-add-modal__input">
                            <CardCvcElement
                                options={{style: ELEMENT_STYLE}}
                                onChange={onElementChange("cvc")}
                            />
                        </div>
                    </label>

                    <label className="card-add-modal__field">
                        <span className="card-add-modal__lbl">Zip</span>
                        <PatternFormat
                            value={postalCode}
                            valueIsNumericString={true}
                            type="tel"
                            format="#####"
                            placeholder="00000"
                            className="card-add-modal__input"
                            onValueChange={postalCodeOnChange}
                            autoComplete="postal-code"
                        />
                    </label>
                </div>

                <div className="card-add-modal__cta-row">
                    <button
                        type="button"
                        className="card-add-modal__reset"
                        onClick={resetForm}
                    >
                        Reset
                    </button>
                    <button
                        type="submit"
                        className="card-add-modal__submit"
                        disabled={!isValid || submitting}
                    >
                        {submitting ? "Adding…" : "Add Card"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function CardPaymentAddModal(props: CardPaymentAddModalProps): ReactElement {
    return (
        <Elements stripe={getStripePromise()}>
            <CardPaymentAddModalForm {...props} />
        </Elements>
    );
}

export default CardPaymentAddModal;
